import { useState, useEffect, useCallback } from "react";
import api, { getAuthToken } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Loader2, RefreshCw, FileSpreadsheet, FileText, Hourglass, TrendingUp, Filter } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmée",
  in_progress: "En cours",
  completed: "Terminée",
  cancelled: "Annulée",
};

const PACK_COLORS = { M: "#10B981", L: "#3B82F6", XL: "#F59E0B" };

/**
 * Reusable Pack-Bookings report. Modes:
 *  - mode="driver"  → /driver/reports/pack-bookings, no driver filter, no export
 *  - mode="admin"   → /admin/reports/pack-bookings, driver filter + export
 */
export default function PackBookingsReport({ mode = "driver", drivers = [] }) {
  const isAdmin = mode === "admin";
  const [filters, setFilters] = useState({ driver_id: "", date_from: "", date_to: "" });
  const [data, setData] = useState({ rows: [], totals: { count: 0, count_refunded: 0, total_amount: 0, total_hours: 0, by_pack_amount: { M: 0, L: 0, XL: 0 } } });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (isAdmin && filters.driver_id) params.driver_id = filters.driver_id;
      const url = isAdmin ? "/admin/reports/pack-bookings" : "/driver/reports/pack-bookings";
      const { data: payload } = await api.get(url, { params });
      setData(payload);
    } catch (e) {
      toast.error("Erreur de chargement", { description: e.response?.data?.detail || "" });
    } finally { setLoading(false); }
  }, [isAdmin, filters]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async (fmt) => {
    if (!isAdmin) return;
    setExporting(fmt);
    try {
      const qs = new URLSearchParams({ format: fmt });
      if (filters.date_from) qs.set("date_from", filters.date_from);
      if (filters.date_to) qs.set("date_to", filters.date_to);
      if (filters.driver_id) qs.set("driver_id", filters.driver_id);
      const token = getAuthToken();
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/pack-bookings/export?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Échec export");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pack_bookings_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error("Échec export", { description: e.message });
    } finally { setExporting(""); }
  };

  const T = data.totals;

  return (
    <div className="space-y-5" data-testid={`pack-report-${mode}`}>
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-2xl p-5 text-white flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Hourglass className="w-5 h-5 text-[#FACC15]" />
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Rapport — Réservations payées en Pack</h2>
          </div>
          <p className="text-sm text-white/70">{isAdmin ? "Vue globale plateforme, filtrable par chauffeur et par période." : "Détail des réservations couvertes par les Pack Heures de vos passagers."}</p>
        </div>
        <Button onClick={load} size="sm" variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="pack-report-reload-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#CBD5E1] rounded-xl p-3" data-testid="pack-report-filters">
        <div className="flex items-center gap-2 mb-2 text-xs text-[#64748B]">
          <Filter className="w-3.5 h-3.5" />
          <span className="font-semibold uppercase tracking-wide">Filtres</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {isAdmin && (
            <select
              className="h-9 border border-[#CBD5E1] rounded-md px-2 text-sm bg-white"
              value={filters.driver_id}
              onChange={(e) => setFilters((f) => ({ ...f, driver_id: e.target.value }))}
              data-testid="filter-driver-select"
            >
              <option value="">Tous les chauffeurs</option>
              {drivers.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
            </select>
          )}
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className="h-9" data-testid="filter-date-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className="h-9" data-testid="filter-date-to" />
        </div>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Button onClick={() => handleExport("csv")} disabled={!!exporting} size="sm" variant="outline" className="text-xs" data-testid="export-pack-csv-btn">
              {exporting === "csv" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />} Export CSV
            </Button>
            <Button onClick={() => handleExport("xlsx")} disabled={!!exporting} size="sm" className="text-xs bg-[#10B981] hover:bg-[#059669] text-white" data-testid="export-pack-xlsx-btn">
              {exporting === "xlsx" ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileSpreadsheet className="w-3 h-3 mr-1" />} Export Excel
            </Button>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pack-report-kpis">
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-3">
          <p className="text-[10px] text-[#64748B] uppercase">Réservations validées</p>
          <p className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid="kpi-count">{T.count}</p>
          {T.count_refunded > 0 && <p className="text-[10px] text-[#EF4444]">+ {T.count_refunded} remboursées</p>}
        </div>
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-3">
          <p className="text-[10px] text-[#64748B] uppercase">Montant total</p>
          <p className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid="kpi-amount">{T.total_amount.toLocaleString()} <span className="text-xs font-normal text-[#64748B]">XAF</span></p>
        </div>
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-3">
          <p className="text-[10px] text-[#64748B] uppercase">Heures consommées</p>
          <p className="text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: 'Outfit' }} data-testid="kpi-hours">{T.total_hours}<span className="text-xs font-normal text-[#64748B]"> h</span></p>
        </div>
        <div className="bg-white rounded-xl border border-[#CBD5E1] p-3">
          <p className="text-[10px] text-[#64748B] uppercase">Répartition</p>
          <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
            {["M", "L", "XL"].map((k) => (
              <span key={k} className="text-[11px] font-bold" style={{ color: PACK_COLORS[k] }} data-testid={`kpi-pack-${k}`}>{k}: {(T.by_pack_amount[k] || 0).toLocaleString()}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 text-[#0A2540] animate-spin" /></div>
      ) : data.rows.length === 0 ? (
        <div className="bg-white border border-[#CBD5E1] rounded-xl p-8 text-center" data-testid="no-pack-bookings">
          <p className="text-sm text-[#64748B]">Aucune réservation par pack pour les critères sélectionnés.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-[#CBD5E1] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pack-report-table">
              <thead className="bg-[#F8FAFC] border-b border-[#CBD5E1]">
                <tr>
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3">Date</th>
                  {isAdmin && <th className="text-left text-xs font-semibold text-[#64748B] p-3">Chauffeur</th>}
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3">Passager</th>
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3">Véhicule</th>
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3">Pack</th>
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3">Heures</th>
                  <th className="text-right text-xs font-semibold text-[#64748B] p-3">Montant</th>
                  <th className="text-left text-xs font-semibold text-[#64748B] p-3">Statut</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} className={`border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC] ${r.pack_refunded ? "opacity-60" : ""}`} data-testid={`pack-row-${r.id}`}>
                    <td className="p-3 text-xs text-[#0A2540]">{r.created_at ? new Date(r.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}</td>
                    {isAdmin && <td className="p-3 text-xs text-[#0A2540] font-medium">{r.driver_name || "—"}</td>}
                    <td className="p-3">
                      <p className="text-xs text-[#0A2540] font-medium">{r.passenger_name || "—"}</p>
                      <p className="text-[10px] text-[#64748B]">{r.passenger_phone || ""}</p>
                    </td>
                    <td className="p-3">
                      <p className="text-xs text-[#0A2540]">{r.vehicle_info || "—"}</p>
                      {r.vehicle_plate && <p className="text-[10px] font-mono text-[#0A2540] bg-[#FACC15]/20 inline-block px-1.5 rounded">{r.vehicle_plate}</p>}
                    </td>
                    <td className="p-3">
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: `${PACK_COLORS[r.pack_type]}20`, color: PACK_COLORS[r.pack_type] }}>Pack {r.pack_type}</span>
                    </td>
                    <td className="p-3 text-xs text-[#0A2540] font-semibold">{r.pack_hours_used}h <span className="text-[10px] text-[#64748B]">({r.pack_rate_per_hour.toLocaleString()} F/h)</span></td>
                    <td className="p-3 text-right text-sm text-[#0A2540] font-extrabold" style={{ fontFamily: 'Outfit' }}>{r.total_amount.toLocaleString()} <span className="text-[10px] font-normal text-[#64748B]">XAF</span></td>
                    <td className="p-3">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.pack_refunded ? "bg-[#EF4444]/10 text-[#EF4444]" : r.status === "cancelled" ? "bg-[#94A3B8]/20 text-[#64748B]" : r.status === "completed" ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#3B82F6]/10 text-[#3B82F6]"}`}>
                        {r.pack_refunded ? "Remboursée" : (STATUS_LABELS[r.status] || r.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-[#F8FAFC] border-t border-[#CBD5E1] px-3 py-2 flex items-center justify-between text-xs">
            <span className="text-[#64748B]">{data.rows.length} ligne{data.rows.length > 1 ? "s" : ""}</span>
            <span className="text-[#0A2540] font-semibold inline-flex items-center gap-1"><TrendingUp className="w-3 h-3 text-[#10B981]" /> Total validé : <strong style={{ fontFamily: 'Outfit' }} data-testid="footer-total">{T.total_amount.toLocaleString()} XAF</strong></span>
          </div>
        </div>
      )}
    </div>
  );
}
